import React from "react";
import { Alert } from "@mui/material";
import Subheader from "@components/Subheader/Subheader";
import BayHireEntryStateMessage from "@features/bay-hire/components/BayHireEntryStateMessage";
import { useBayHireEntryState } from "@features/bay-hire/hooks/useBayHireEntryState";
import PageLayout from "@layouts/PageLayout";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";

export interface BayHireFormPageProps {
  /** `bay-hire.request-form-title` for create/resubmit/renew; `bay-hire.update-form-title` for update - see BayHireContainer's original isFormView title switch (DFT-0044 FE-15). */
  titleKey: "bay-hire.request-form-title" | "bay-hire.update-form-title";
  titleDefault: string;
  submitError?: string | null;
  submitWarning?: string | null;
  children: React.ReactNode;
}

const BayHireFormPage: React.FC<BayHireFormPageProps> = ({
  titleKey,
  titleDefault,
  submitError,
  submitWarning,
  children,
}) => {
  const { t, i18n } = useTranslation();
  const dir = i18n.language === "ar" ? "rtl" : "ltr";
  const { entryState, isOperationsUser } = useBayHireEntryState();

  if (isOperationsUser) {
    return <Navigate to="/bay-hire/operations" replace />;
  }

  return (
    <PageLayout dir={dir}>
      <Subheader
        testId="bay-hire-form-subheader"
        title={t(titleKey, { defaultValue: titleDefault })}
      />
      <div data-testid="bay-hire-form-page" className="mt-8 px-24">
        {entryState === "allowed" ? (
          <>
            {submitError && (
              <Alert severity="error" sx={{ mb: 2 }} data-testid="bay-hire-submit-error">
                {submitError}
              </Alert>
            )}
            {submitWarning && (
              <Alert severity="warning" sx={{ mb: 2 }} data-testid="bay-hire-submit-warning">
                {submitWarning}
              </Alert>
            )}
            {children}
          </>
        ) : (
          <BayHireEntryStateMessage entryState={entryState} />
        )}
      </div>
    </PageLayout>
  );
};

export default BayHireFormPage;
